import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { TourProvider } from '@reactour/tour';
import { useTranslatedSteps } from './stepsTour';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import { MarketplaceContent } from './marketplace/Marketplace';
import { MyIntegrationsContent } from './marketplace/MyIntegrations';
import DidarAdvancedSettings, {
  useHasActiveDidarIntegration,
} from './marketplace/DidarAdvancedSettings';
import {
  DIDAR_SETTING_SNACKBAR_OPTIONS,
  buildAidaSettingsPathAfterDidarCleanup,
  didarSettingSearchParamsNeedCleanup,
  isDidarMissingIntegrationFlag,
} from './marketplace/didarSettingRoute';
import { useTranslation } from '../contexts/TranslationContext';

const DIDAR_ADVANCED_TAB_INDEX = 2;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`connections-tabpanel-${index}`}
      aria-labelledby={`connections-tab-${index}`}
      style={{ width: '100%', height: '100%', display: value === index ? 'block' : 'none' }}
    >
      {value === index && <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>{children}</Box>}
    </div>
  );
};

interface ConnectionsProps {
  /** 0 = marketplace, 1 = my integrations, 2 = Didar advanced (when available) */
  initialSubTab?: number;
}

const Connections: React.FC<ConnectionsProps> = ({ initialSubTab }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const didarMissingSnackbarShown = useRef(false);
  const didarDeepLinkHandledRef = useRef(false);
  const { hasDidar, loading: didarCheckLoading, didarUserIntegration } =
    useHasActiveDidarIntegration();
  const [value, setValue] = useState(() => {
    if (initialSubTab === 1) return 1;
    if (initialSubTab === DIDAR_ADVANCED_TAB_INDEX) return DIDAR_ADVANCED_TAB_INDEX;
    return 0;
  });

  const maxTabIndex = hasDidar ? DIDAR_ADVANCED_TAB_INDEX : 1;

  const showDidarMissingSnackbar = () => {
    if (didarMissingSnackbarShown.current) return;
    didarMissingSnackbarShown.current = true;
    enqueueSnackbar(
      t('marketplace.connections.didarMissingIntegration'),
      DIDAR_SETTING_SNACKBAR_OPTIONS,
    );
  };

  useEffect(() => {
    if (initialSubTab === undefined || initialSubTab < 0 || initialSubTab > maxTabIndex) {
      return;
    }
    setValue(initialSubTab);
  }, [initialSubTab, maxTabIndex]);

  useEffect(() => {
    if (didarCheckLoading) {
      setValue((current) =>
        current === DIDAR_ADVANCED_TAB_INDEX ? 0 : current,
      );
      return;
    }

    if (!hasDidar && value === DIDAR_ADVANCED_TAB_INDEX) {
      setValue(0);
    }
  }, [hasDidar, didarCheckLoading, value]);

  useEffect(() => {
    if (didarCheckLoading || hasDidar || didarDeepLinkHandledRef.current) {
      return;
    }

    const triedDidarSettings =
      initialSubTab === DIDAR_ADVANCED_TAB_INDEX ||
      searchParams.get('subtab') === String(DIDAR_ADVANCED_TAB_INDEX);

    if (!triedDidarSettings) {
      return;
    }

    didarDeepLinkHandledRef.current = true;
    setValue(0);

    if (!isDidarMissingIntegrationFlag(searchParams)) {
      showDidarMissingSnackbar();
    }

    if (didarSettingSearchParamsNeedCleanup(searchParams)) {
      navigate(buildAidaSettingsPathAfterDidarCleanup(searchParams), { replace: true });
    }
  }, [hasDidar, didarCheckLoading, initialSubTab, searchParams, navigate]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const steps = [
    ...useTranslatedSteps().steps_marketplace,
    ...useTranslatedSteps().steps_myIntegrations,
  ];

  return (
    <TourProvider steps={steps}>
      <VideoProvider videoUrl={videoUrls.marketPlace} videoTitle={videoTitles.marketPlace}>
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', px:1}}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChange} aria-label="connections tabs">
              <Tab label="اطرافیان آیدا" id="connections-tab-0" aria-controls="connections-tabpanel-0" />
              <Tab label="دوستان آیدا" id="connections-tab-1" aria-controls="connections-tabpanel-1" />
              {hasDidar && !didarCheckLoading && (
                <Tab
                  label={t('marketplace.connections.didarAdvancedTab')}
                  id="connections-tab-2"
                  aria-controls="connections-tabpanel-2"
                />
              )}
            </Tabs>
          </Box>
          <Box sx={{ width: '100%', flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
            <TabPanel value={value} index={0}>
              <MarketplaceContent />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <MyIntegrationsContent onBrowseMarketplace={() => setValue(0)} />
            </TabPanel>
            {hasDidar && !didarCheckLoading && (
              <TabPanel value={value} index={DIDAR_ADVANCED_TAB_INDEX}>
                <DidarAdvancedSettings didarUserIntegration={didarUserIntegration} />
              </TabPanel>
            )}
          </Box>
        </Box>
      </VideoProvider>
    </TourProvider>
  );
};

export default Connections;

